import { IconButton, Tooltip } from "@material-ui/core";
import classnames from "classnames";
import { clone, contains, map } from "ramda";
import * as React from "react";
import { connect } from "react-redux";
import {
  addUserToReadByForComments,
  AddUserToReadByForCommentsType
} from "src/commentView/actions";
import { CommentIcon } from "src/commentView/CommentIcon";
import { CommentPopover } from "src/commentView/CommentPopover";
import {
  getUnreadComments,
  sortAccountCommentsByPeriod
} from "src/commentView/helpers";
import {
  AccountComments,
  Comment,
  CommentsByPeriod
} from "src/commentView/types";
import { getUserId } from "src/login/selector";
import { isRequesting } from "src/requestProgress/helpers";
import { actions, RequestProgressState } from "src/requestProgress/reducer";
import { getRequestProgress } from "src/requestProgress/selector";
import { v4 as uuid } from "uuid";
import { AddCommentIcon } from "../../commentView/AddCommentIcon";
import { QuiqIcon } from "../../shared/QuiqIcon";
import { State } from "../../state";
import { getIsRowHighlighted } from "../model/highlightedRows/selector";
import { AccountPresenter } from "../model/presenter/types";
import { getPeriodSelectedFn } from "../model/selectedCells/selector";
import {
  getAccountComments,
  getComments,
  getLanguage,
  getSpreadEntityId,
  getSpreadId,
  getSpreadIsCompleted
} from "../model/spread/selector";
import AccountMetadata from "./AccountMetadata";
import ArithmeticFlyout from "./ArithmeticFlyout";
import { accountValueKey } from "./helpers";
import { MoreHorizontalIcon } from "./MoreHorizontalIcon";
import { RemapIcon } from "./RemapIcon";
import { enableTagMode, setTagConfig } from "./Tag/actions";
import { AddIcon } from "./Tag/AddIcon";
import { getTagMode } from "./Tag/selector";
import "./Tag/TagRow.scss";
import ValueCell from "./ValueCell";

interface ValueAccountRowProps {
  account: AccountPresenter;
  hasHiddenAccounts: boolean;
  hierarchyLevel: number;
  expanded: boolean;
  onToggleHiddenAccounts: () => void;
  periodSelected: number;
  isSpreadEditable: boolean;
  mappingNodeId: string | null;
  isRowHighlighted: boolean;
  handleRemapClick: () => void;
  requestProgress: RequestProgressState;
  spreadId: string;
  accountComments: AccountComments | null;
  userId: string;
  periods: string[];
  addUserToReadByForComments?: AddUserToReadByForCommentsType;
  enableTagMode: typeof enableTagMode;
  tagMode: boolean;
  setTagConfig: typeof setTagConfig;
  language?: string | null;
  entityId: string;
}

interface ValueAccountRowState {
  hasUnreadComments: { [period: string]: boolean };
  commentsByPeriod: CommentsByPeriod;
  anchorElByPeriod: { [period: string]: HTMLElement | null };
}

interface HiddenAccountsProps {
  expanded: boolean;
  hasHiddenAccounts: boolean;
  onToggleHiddenAccounts: () => void;
}

export const HiddenAccountsToggle: React.SFC<HiddenAccountsProps> = ({
  hasHiddenAccounts,
  expanded,
  onToggleHiddenAccounts
}) => {
  const onClick = () => onToggleHiddenAccounts();

  return hasHiddenAccounts ? (
    <IconButton
      className={classnames("HiddenAccountsToggle__indicator", {
        "HiddenAccountsToggle__indicator--expanded": expanded
      })}
      onClick={onClick}
      children={<MoreHorizontalIcon />}
    />
  ) : null;
};

export class ValueAccountRow extends React.Component<
  ValueAccountRowProps,
  ValueAccountRowState
> {
  constructor(props: ValueAccountRowProps) {
    super(props);
    this.state = {
      hasUnreadComments: {},
      commentsByPeriod: {},
      anchorElByPeriod: {}
    };
  }

  public componentDidMount() {
    const { account, accountComments } = this.props;

    if (!accountComments || !accountComments[account.id]) {
      return;
    }

    const commentsByPeriod = sortAccountCommentsByPeriod(
      accountComments[account.id]
    );

    const hasUnreadCommentsByPeriod = {};

    this.props.periods.forEach(period => {
      const hasUnreadComments =
        getUnreadComments(commentsByPeriod[period] || [], this.props.userId)
          .length > 0;
      hasUnreadCommentsByPeriod[period] = hasUnreadComments;
    });

    this.setState({
      commentsByPeriod,
      hasUnreadComments: hasUnreadCommentsByPeriod
    });
  }

  public openTagLine = () => {
    this.props.enableTagMode();
    this.props.setTagConfig({
      parentNodeId: this.props.account.id,
      activeAreaId: "",
      groupId: uuid(),
      periodCount: this.props.periods.length,
      period1: this.props.periods[0],
      period2: this.props.periods[1]
    });
  };

  public render() {
    const {
      account,
      hasHiddenAccounts,
      hierarchyLevel,
      onToggleHiddenAccounts,
      periodSelected,
      expanded,
      isSpreadEditable,
      isRowHighlighted,
      entityId
    } = this.props;

    const isLoading =
      isRequesting(actions.undoRule) ||
      isRequesting(actions.remapAccount) ||
      isRequesting(actions.mapUnmappedAccount);

    const isRowMapping = account.id === this.props.mappingNodeId;
    const isCommenting = Object.keys(this.state.anchorElByPeriod).filter(
      anchorElKey => this.state.anchorElByPeriod[anchorElKey]
    ).length;

    const language = this.props.language;
    const name =
      (language &&
        account.translatedNames &&
        account.translatedNames[language]) ||
      account.name;

    return (
      <React.Fragment>
        <div
          className={classnames(
            `AccountRow__container AccountRow__container--value AccountRow__container--period-${account.periodValues.length}`,
            { "AccountRow--active-mode": isRowMapping || isCommenting },
            { "AccountRow--mapping-mode": isRowMapping },
            { "AccountRow--commenting-mode": isCommenting },
            { "AccountRow--selected": isRowHighlighted && !expanded }
          )}
        >
          <div
            className={`AccountRow__hover-container AccountRow__hover-container--level-${hierarchyLevel}`}
          >
            <div className={"AccountRow__name AccountRow__name--value"}>
              <div className="AccountRow__text AccountRow__text--value">
                {name}
                <AccountMetadata account={account} />
              </div>
              <HiddenAccountsToggle
                onToggleHiddenAccounts={onToggleHiddenAccounts}
                hasHiddenAccounts={hasHiddenAccounts}
                expanded={expanded}
              />
            </div>
            {account.periodValues.map(
              (periodValue, index) => (
                <div
                  key={accountValueKey(account, index) + "div"}
                  className="AccountRow__text--container"
                >
                  {this.props.periods && (
                    <CommentPopover
                      comments={this.commentsForPeriod(
                        this.props.periods[index]
                      )}
                      isOpen={Boolean(
                        this.anchorElForPeriod(this.props.periods[index])
                      )}
                      onClose={this.handlePopoverClose(
                        this.props.periods[index]
                      )}
                      anchorEl={this.anchorElForPeriod(
                        this.props.periods[index]
                      )}
                      updateComments={this.updateComments(
                        this.props.periods[index]
                      )}
                      spreadId={this.props.spreadId}
                      accountId={this.props.account.id}
                      accountName={name}
                      dontShowAccountLabel={true}
                      period={this.props.periods[index]}
                    />
                  )}

                  <ValueCell
                    account={account}
                    lens={account.valueLensesByPeriod[index]}
                    periodValue={periodValue}
                    period={index}
                    key={accountValueKey(account, index)}
                  >
                    {(periodValue || periodValue === 0) && this.props.periods && (
                      <IconButton
                        className="AccountRow__buttons__comment AccountRow__button AccountRow__buttons"
                        color={"inherit"}
                        onClick={this.openCommentPopover(
                          this.props.periods[index]
                        )}
                      >
                        {this.hasCommentsForPeriod(
                          this.props.periods[index]
                        ) ? (
                          <React.Fragment>
                            {this.renderReadStatus(this.props.periods[index])}
                            <CommentIcon />
                          </React.Fragment>
                        ) : (
                          <AddCommentIcon
                            className="AccountRow__button--add-comment"
                            id={account.id}
                          />
                        )}
                      </IconButton>
                    )}
                  </ValueCell>
                </div>
              ),
              this
            )}

            {isSpreadEditable && (
              <div className="AccountRow__map">
                <div className={classnames("AccountRow__buttons")}>
                  <div className="AccountRow__map">
                    <Tooltip
                      classes={{ tooltip: "ToolTip-container" }}
                      title="Remap Child Accounts"
                      placement="top"
                    >
                      <QuiqIcon
                        className="AccountRow__buttons__map AccountRow__button"
                        children={<RemapIcon />}
                        color={"inherit"}
                        onClick={this.props.handleRemapClick}
                        isDisabled={!entityId}
                        isLoading={isRowMapping && isLoading}
                      />
                    </Tooltip>
                    <Tooltip
                      classes={{ tooltip: "ToolTip-container" }}
                      title="Add Account"
                      placement="top"
                    >
                      <QuiqIcon
                        className="AccountRow__buttons__tag AccountRow__button"
                        children={<AddIcon />}
                        color={"inherit"}
                        onClick={this.openTagLine}
                        containerClassName="AccountRow__tagButton__container"
                      />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </div>
          {periodSelected > -1 &&
          isSpreadEditable &&
          !this.props.mappingNodeId ? (
            <ArithmeticFlyout
              account={account}
              lens={account.valueLensesByPeriod[periodSelected]}
              period={periodSelected}
              periodValue={account.periodValues[periodSelected]}
              periodString={this.props.periods[periodSelected]}
            />
          ) : null}
        </div>
      </React.Fragment>
    );
  }

  private handlePopoverClose = (period: string) => (
    event?: React.SyntheticEvent<{}>
  ) => {
    const anchorElByPeriod = clone(this.state.anchorElByPeriod);

    anchorElByPeriod[period] = null;

    this.setState({
      anchorElByPeriod
    });

    this.markCommentsAsRead(period);
  };

  private openCommentPopover = (period: string) => (
    event: React.MouseEvent<HTMLElement>
  ) => {
    const anchorElByPeriod = clone(this.state.anchorElByPeriod);

    anchorElByPeriod[period] = event.currentTarget;

    this.setState({
      anchorElByPeriod
    });
  };

  private anchorElForPeriod = (period: string) => {
    return this.state.anchorElByPeriod[period];
  };

  private commentsForPeriod = (period: string) => {
    return (period && this.state.commentsByPeriod[period]) || [];
  };

  private updateComments = (period: string) => (comments: Comment[]) => {
    const commentsByPeriod = clone(this.state.commentsByPeriod);

    commentsByPeriod[period] = comments;

    this.setState({
      commentsByPeriod
    });
  };

  private hasCommentsForPeriod = (period: string) => {
    return (
      this.state.commentsByPeriod[period] &&
      this.state.commentsByPeriod[period].length > 0
    );
  };

  private renderReadStatus = (period: string) => {
    const hasUnread = this.state.hasUnreadComments[period];

    return (
      <div
        className={classnames(
          "AccountRow__read-status",
          {
            "AccountRow__read-status--unread": hasUnread
          },
          {
            "AccountRow__read-status--read": !hasUnread
          }
        )}
      />
    );
  };

  private markCommentsAsRead = (period: string) => {
    const commentsByPeriod = clone(this.state.commentsByPeriod);
    const comments = commentsByPeriod[period];
    const { userId } = this.props;
    if (comments) {
      const unreadComments = getUnreadComments(comments, userId);

      if (this.props.addUserToReadByForComments) {
        this.props.addUserToReadByForComments({
          comments: unreadComments,
          userId: this.props.userId
        });
      }

      const commentsMarkedAsRead = map((comment: Comment) => {
        if (comment.readBy && !contains(userId, comment.readBy)) {
          comment.readBy.push(userId);
        }
        return comment;
      }, comments);

      const hasUnreadComments = clone(this.state.hasUnreadComments);
      hasUnreadComments[period] = false;
      commentsByPeriod[period] = commentsMarkedAsRead;

      this.setState({
        commentsByPeriod,
        hasUnreadComments
      });
    }
  };
}

const mapStateToProps = (state: State, ownProps: any) => ({
  periodSelected: getPeriodSelectedFn(state)(
    ownProps.account.spreadSheetType,
    ownProps.account.id
  ),
  isSpreadEditable: !getSpreadIsCompleted(state),
  isRowHighlighted: getIsRowHighlighted(state)(ownProps.account.id),
  requestProgress: getRequestProgress(state),
  spreadId: getSpreadId(state),
  accountComments: getAccountComments(state),
  userId: getUserId(state),
  comments: getComments(state),
  tagMode: getTagMode(state),
  language: getLanguage(state),
  entityId: getSpreadEntityId(state)
});

export default connect(
  mapStateToProps,
  {
    addUserToReadByForComments: addUserToReadByForComments.action,
    enableTagMode,
    setTagConfig
  }
)(ValueAccountRow);



// WEBPACK FOOTER //
// ./src/spread/chartOfAccounts/ValueAccountRow.tsx